import React, { useState } from 'react';
import {
  CreditCardIcon,
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  TrophyIcon,
  InformationCircleIcon
} from '../utils/icons';

// Mock data for demonstration
const mockCards = [
  {
    id: '1',
    name: 'Chase Sapphire Preferred',
    rewardsBalance: 32500,
    bestCategories: ['Travel', 'Dining'],
    earningRates: [
      { category: 'Travel', rate: '2x' },
      { category: 'Dining', rate: '2x' },
      { category: 'Other', rate: '1x' }
    ]
  },
  {
    id: '2',
    name: 'Amex Gold',
    rewardsBalance: 18000,
    bestCategories: ['Groceries', 'Dining'],
    earningRates: [
      { category: 'Groceries', rate: '4x' },
      { category: 'Dining', rate: '4x' },
      { category: 'Other', rate: '1x' }
    ]
  }
];

const mockSpending = [
  { id: 's1', category: 'Groceries', amount: 250, bestCard: 'Amex Gold' },
  { id: 's2', category: 'Travel', amount: 120, bestCard: 'Chase Sapphire Preferred' },
  { id: 's3', category: 'Dining', amount: 90, bestCard: 'Amex Gold' }
];

const CreditCardRewards = () => {
  const [cards, setCards] = useState(mockCards);
  const [spending, setSpending] = useState(mockSpending);
  const [goal, setGoal] = useState({ name: 'Free Flight', targetPoints: 50000 });

  // Calculate total rewards
  const totalRewards = cards.reduce((sum, card) => sum + card.rewardsBalance, 0);
  const progress = Math.min(100, Math.round((totalRewards / goal.targetPoints) * 100));

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-4 flex items-center">
        <TrophyIcon className="h-8 w-8 text-yellow-500 mr-2" aria-hidden="true" />
        Credit Card Rewards Maximization
      </h1>
      <p className="mb-6 text-gray-600">
        Track, optimize, and learn how to maximize your credit card rewards. Link your cards, get personalized recommendations, and reach your reward goals faster.
      </p>

      {/* Rewards Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow p-4 flex flex-col items-center">
          <CurrencyDollarIcon className="h-8 w-8 text-green-500 mb-2" aria-hidden="true" />
          <div className="text-lg font-semibold">Total Rewards</div>
          <div className="text-2xl text-green-600 font-bold">{totalRewards.toLocaleString()} pts</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 flex flex-col items-center">
          <ArrowTrendingUpIcon className="h-8 w-8 text-blue-500 mb-2" aria-hidden="true" />
          <div className="text-lg font-semibold">Goal Progress</div>
          <div className="w-full bg-gray-200 rounded-full h-3 mt-2 mb-1">
            <div style={{ width: `${progress}%` }} className="bg-yellow-400 h-3 rounded-full transition-all"></div>
          </div>
          <div className="text-sm text-gray-700">{progress}% to {goal.name} ({goal.targetPoints.toLocaleString()} pts)</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 flex flex-col items-center">
          <CreditCardIcon className="h-8 w-8 text-purple-500 mb-2" aria-hidden="true" />
          <div className="text-lg font-semibold">Linked Cards</div>
          <div className="text-2xl text-purple-600 font-bold">{cards.length}</div>
        </div>
      </div>

      {/* Top Cards by Category */}
      <div className="mb-8">
        <h2 className="text-xl font-bold mb-2">Best Card for Each Category</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {spending.map((item) => (
            <div key={item.id} className="bg-blue-50 rounded-lg p-4 flex flex-col items-center">
              <div className="font-semibold text-blue-700">{item.category}</div>
              <div className="text-lg mt-1 mb-2">Use <span className="font-bold text-blue-900">{item.bestCard}</span></div>
              <div className="text-sm text-gray-600">${item.amount} spent this month</div>
            </div>
          ))}
        </div>
      </div>

      {/* Linked Cards Table */}
      <div className="mb-8">
        <h2 className="text-xl font-bold mb-2">Your Credit Cards</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white rounded-lg shadow">
            <thead>
              <tr className="bg-gray-100 text-gray-700">
                <th className="py-2 px-4 text-left">Card</th>
                <th className="py-2 px-4 text-left">Rewards Balance</th>
                <th className="py-2 px-4 text-left">Best Categories</th>
                <th className="py-2 px-4 text-left">Earning Rates</th>
              </tr>
            </thead>
            <tbody>
              {cards.map((card) => (
                <tr key={card.id} className="border-b last:border-b-0">
                  <td className="py-2 px-4 font-semibold flex items-center">
                    <CreditCardIcon className="h-6 w-6 text-purple-400 mr-2" aria-hidden="true" />
                    {card.name}
                  </td>
                  <td className="py-2 px-4">{card.rewardsBalance.toLocaleString()} pts</td>
                  <td className="py-2 px-4">{card.bestCategories.join(', ')}</td>
                  <td className="py-2 px-4">
                    {card.earningRates.map((rate, i) => (
                      <div key={i}>{rate.category}: <span className="font-semibold">{rate.rate}</span></div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Educational Content */}
      <div className="mb-8">
        <h2 className="text-xl font-bold mb-2 flex items-center">
          <InformationCircleIcon className="h-6 w-6 text-blue-400 mr-1" aria-hidden="true" />
          How to Maximize Your Credit Card Rewards
        </h2>
        <ul className="list-disc list-inside text-gray-700 space-y-1">
          <li>Use the right card for each purchase category to earn the most points or cash back.</li>
          <li>Take advantage of sign-up bonuses and special offers.</li>
          <li>Pay your balance in full each month to avoid interest charges.</li>
          <li>Track your progress toward reward goals and adjust your strategy as needed.</li>
        </ul>
      </div>
    </div>
  );
};

export default CreditCardRewards;
